import GuestLayout from "@/components/layout-guest";
import { useGuestData } from "@/hooks/use-guest";
import { useRoute, Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Wifi, Clock, ArrowRight, DoorOpen, BookOpen, Sparkles, ShieldCheck, Copy, Check, LifeBuoy, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { useState } from "react";
import { useLanguage } from "@/hooks/use-language";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Section } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import { MessageSquare } from "lucide-react";
import GuestChat from "@/components/guest-chat";
import GuestLiveChat from "@/components/guest-live-chat";

const categoryConfig: Record<string, { icon: typeof DoorOpen; label: string; labelEs: string; color: string }> = {
  "check-in": { icon: DoorOpen, label: "Check-in", labelEs: "Llegada", color: "text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400" },
  "stay": { icon: BookOpen, label: "During Stay", labelEs: "Durante Estancia", color: "text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400" },
  "amenities": { icon: Sparkles, label: "Amenities", labelEs: "Amenidades", color: "text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-400" },
  "rules": { icon: ShieldCheck, label: "House Rules", labelEs: "Reglas", color: "text-orange-600 bg-orange-50 dark:bg-orange-950 dark:text-orange-400" },
};

export default function GuestHome() {
  const [match, params] = useRoute("/g/:slug");
  const slug = params?.slug || "";
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("t");
  const { lang } = useLanguage();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [guestTicketOpen, setGuestTicketOpen] = useState(false);
  const [guestTicketSuccess, setGuestTicketSuccess] = useState(false);
  const [guestTicketForm, setGuestTicketForm] = useState({
    guestName: "",
    guestEmail: "",
    subject: "",
    category: "general",
    body: "",
  });

  const queryClient = useQueryClient();
  const [selectedGuestTicket, setSelectedGuestTicket] = useState<any>(null);
  const [guestReplyText, setGuestReplyText] = useState("");
  const [guestReplyName, setGuestReplyName] = useState("");

  const guestTicketMutation = useMutation({
    mutationFn: async (data: typeof guestTicketForm) => {
      const url = `/api/guest/${slug}/tickets${token ? `?t=${token}` : ""}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to submit ticket");
      }
      return res.json();
    },
    onSuccess: () => {
      setGuestTicketSuccess(true);
      setGuestTicketForm({ guestName: "", guestEmail: "", subject: "", category: "general", body: "" });
      queryClient.invalidateQueries({ queryKey: ["/api/g", slug, "tickets"] });
    },
  });

  const { data: guestTickets = [] } = useQuery<any[]>({
    queryKey: ["/api/g", slug, "tickets"],
    queryFn: async () => {
      const res = await fetch(`/api/g/${slug}/tickets${token ? `?t=${token}` : ""}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!slug && !!token,
  });

  const { data: guestTicketMessages = [] } = useQuery<any[]>({
    queryKey: ["/api/g", slug, "tickets", selectedGuestTicket?.id, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/g/${slug}/tickets/${selectedGuestTicket.id}/messages${token ? `?t=${token}` : ""}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedGuestTicket?.id,
  });

  const guestReplyMutation = useMutation({
    mutationFn: async ({ ticketId, body, guestName }: { ticketId: number; body: string; guestName: string }) => {
      const res = await fetch(`/api/g/${slug}/tickets/${ticketId}/messages${token ? `?t=${token}` : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body, guestName }),
      });
      if (!res.ok) throw new Error("Failed to send reply");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/g", slug, "tickets", selectedGuestTicket?.id, "messages"] });
      setGuestReplyText("");
    },
  });

  const { data, isLoading, error } = useGuestData(slug, token);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-48 w-full rounded-md" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-28 rounded-md" />
          <Skeleton className="h-28 rounded-md" />
        </div>
        <Skeleton className="h-20 w-full rounded-md" />
        <Skeleton className="h-20 w-full rounded-md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
        <ShieldCheck className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-display font-bold mb-2">
          {lang === 'en' ? "Unable to Load" : "No se pudo cargar"}
        </h2>
        <p className="text-muted-foreground">
          {lang === 'en' ? "Please check your link and try again." : "Por favor verifique su enlace e intente de nuevo."}
        </p>
      </div>
    );
  }

  const { apartment, building, sections } = data;
  const tokenParam = token ? `?t=${token}` : '';

  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <GuestLayout slug={slug} title={apartment.name}>
      <div className="rounded-md overflow-hidden shadow-lg aspect-video bg-muted relative mb-6" data-testid="img-hero">
        {apartment.heroImage ? (
          <img src={apartment.heroImage} alt={apartment.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-5">
          <h1 className="text-white text-2xl font-display font-bold" data-testid="text-apartment-name">{apartment.name}</h1>
          <p className="text-white/80 text-sm mt-1" data-testid="text-building-address">{building.address}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <Card
          className="cursor-pointer"
          onClick={() => copyToClipboard(apartment.wifiPassword || '', 'wifi')}
          data-testid="card-wifi"
        >
          <CardContent className="p-4 flex flex-col items-center text-center gap-2">
            <div className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <Wifi className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">WIFI</p>
              <p className="font-bold text-sm truncate max-w-[120px]" data-testid="text-wifi-ssid">{apartment.wifiSsid}</p>
              <div className="flex items-center gap-1 justify-center mt-1">
                <p className="font-mono text-xs text-muted-foreground select-all" data-testid="text-wifi-password">{apartment.wifiPassword}</p>
                {copiedField === 'wifi' ? (
                  <Check className="w-3 h-3 text-green-500" />
                ) : (
                  <Copy className="w-3 h-3 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-checkout-time">
          <CardContent className="p-4 flex flex-col items-center text-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-950 flex items-center justify-center text-orange-600 dark:text-orange-400">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                {lang === 'en' ? 'CHECKOUT' : 'SALIDA'}
              </p>
              <p className="font-bold text-lg" data-testid="text-checkout-time">{apartment.checkoutTime}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3 mb-6">
        <h2 className="text-lg font-display font-bold">
          {lang === 'en' ? "Property Guide" : "Guia de la Propiedad"}
        </h2>
        {sortedSections.map((section: Section) => {
          const config = categoryConfig[section.category] || categoryConfig["stay"];
          const Icon = config.icon;
          const title = lang === 'en' ? section.titleEn : section.titleEs;
          const summary = lang === 'en' ? section.summaryEn : section.summaryEs;

          return (
            <Link key={section.id} href={`/g/${slug}/section/${section.id}${tokenParam}`}>
              <Card className="hover-elevate cursor-pointer" data-testid={`card-section-${section.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm" data-testid={`text-section-title-${section.id}`}>{title}</p>
                    {summary && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{summary}</p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            </Link>
          );
        })}
        {sortedSections.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            {lang === 'en' ? "No property information available." : "No hay informacion de la propiedad disponible."}
          </p>
        )}
      </div>

      <div className="mb-6">
        <Dialog open={guestTicketOpen} onOpenChange={(open) => {
          setGuestTicketOpen(open);
          if (!open) setGuestTicketSuccess(false);
        }}>
          <DialogTrigger asChild>
            <Card className="hover-elevate cursor-pointer" data-testid="card-need-help">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 text-amber-600 bg-amber-50 dark:bg-amber-950 dark:text-amber-400">
                  <LifeBuoy className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" data-testid="text-need-help-title">
                    {lang === "en" ? "Need Help?" : "Necesitas Ayuda?"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {lang === "en" ? "Submit a support ticket" : "Enviar un ticket de soporte"}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {lang === "en" ? "Submit Support Ticket" : "Enviar Ticket de Soporte"}
              </DialogTitle>
              <DialogDescription>
                {lang === "en" ? "We'll get back to you as soon as possible." : "Le responderemos lo antes posible."}
              </DialogDescription>
            </DialogHeader>

            {guestTicketSuccess ? (
              <div className="text-center py-6 space-y-3" data-testid="text-ticket-success">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500" />
                <p className="font-medium">
                  {lang === "en" ? "Ticket Submitted!" : "Ticket Enviado!"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {lang === "en"
                    ? "We've received your request and will respond soon."
                    : "Hemos recibido su solicitud y responderemos pronto."}
                </p>
                <Button variant="outline" onClick={() => { setGuestTicketOpen(false); setGuestTicketSuccess(false); }} data-testid="button-ticket-close">
                  {lang === "en" ? "Close" : "Cerrar"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>{lang === "en" ? "Your Name" : "Su Nombre"}</Label>
                  <Input
                    value={guestTicketForm.guestName}
                    onChange={(e) => setGuestTicketForm((p) => ({ ...p, guestName: e.target.value }))}
                    placeholder={lang === "en" ? "Full name" : "Nombre completo"}
                    data-testid="input-guest-name"
                  />
                </div>
                <div>
                  <Label>{lang === "en" ? "Email (optional)" : "Correo (opcional)"}</Label>
                  <Input
                    type="email"
                    value={guestTicketForm.guestEmail}
                    onChange={(e) => setGuestTicketForm((p) => ({ ...p, guestEmail: e.target.value }))}
                    placeholder={lang === "en" ? "your@email.com" : "su@correo.com"}
                    data-testid="input-guest-email"
                  />
                </div>
                <div>
                  <Label>{lang === "en" ? "Subject" : "Asunto"}</Label>
                  <Input
                    value={guestTicketForm.subject}
                    onChange={(e) => setGuestTicketForm((p) => ({ ...p, subject: e.target.value }))}
                    placeholder={lang === "en" ? "Brief description" : "Descripcion breve"}
                    data-testid="input-guest-ticket-subject"
                  />
                </div>
                <div>
                  <Label>{lang === "en" ? "Category" : "Categoria"}</Label>
                  <Select value={guestTicketForm.category} onValueChange={(v) => setGuestTicketForm((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="select-guest-ticket-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">{lang === "en" ? "General" : "General"}</SelectItem>
                      <SelectItem value="access">{lang === "en" ? "Access" : "Acceso"}</SelectItem>
                      <SelectItem value="maintenance">{lang === "en" ? "Maintenance" : "Mantenimiento"}</SelectItem>
                      <SelectItem value="cleaning_issue">{lang === "en" ? "Cleaning Issue" : "Problema de Limpieza"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === "en" ? "Details" : "Detalles"}</Label>
                  <Textarea
                    value={guestTicketForm.body}
                    onChange={(e) => setGuestTicketForm((p) => ({ ...p, body: e.target.value }))}
                    placeholder={lang === "en" ? "Describe your issue..." : "Describa su problema..."}
                    className="resize-none"
                    data-testid="input-guest-ticket-body"
                  />
                </div>
                {guestTicketMutation.isError && (
                  <p className="text-sm text-destructive" data-testid="text-guest-ticket-error">
                    {(guestTicketMutation.error as Error)?.message || (lang === "en" ? "Something went wrong." : "Algo salio mal.")}
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={() => guestTicketMutation.mutate(guestTicketForm)}
                  disabled={!guestTicketForm.guestName.trim() || !guestTicketForm.subject.trim() || guestTicketMutation.isPending}
                  data-testid="button-guest-submit-ticket"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {guestTicketMutation.isPending
                    ? (lang === "en" ? "Submitting..." : "Enviando...")
                    : (lang === "en" ? "Submit Ticket" : "Enviar Ticket")}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {guestTickets.length > 0 && (
        <div className="space-y-3 mb-6">
          <h2 className="text-lg font-display font-bold">
            {lang === "en" ? "Your Tickets" : "Sus Tickets"}
          </h2>
          {guestTickets.map((ticket: any) => (
            <Card
              key={ticket.id}
              className="hover-elevate cursor-pointer"
              onClick={() => { setSelectedGuestTicket(ticket); setGuestReplyText(""); setGuestReplyName(""); }}
              data-testid={`card-guest-ticket-${ticket.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate" data-testid={`text-guest-ticket-subject-${ticket.id}`}>{ticket.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(ticket.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}
                  </p>
                </div>
                <Badge
                  variant={ticket.status === "open" ? "destructive" : ticket.status === "resolved" ? "default" : "secondary"}
                  data-testid={`badge-guest-ticket-status-${ticket.id}`}
                >
                  {ticket.status === "open" ? (lang === "en" ? "Open" : "Abierto")
                    : ticket.status === "in_progress" ? (lang === "en" ? "In Progress" : "En Proceso")
                    : ticket.status === "resolved" ? (lang === "en" ? "Resolved" : "Resuelto")
                    : (lang === "en" ? "Closed" : "Cerrado")}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedGuestTicket} onOpenChange={(open) => { if (!open) setSelectedGuestTicket(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="text-guest-ticket-detail-subject">
              {selectedGuestTicket?.subject}
            </DialogTitle>
            <DialogDescription>
              {lang === "en" ? "Conversation with property management" : "Conversacion con administracion"}
            </DialogDescription>
          </DialogHeader>
          {selectedGuestTicket && (
            <div className="flex-1 flex flex-col space-y-3 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-3 max-h-[50vh]">
                {guestTicketMessages.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-guest-messages">
                    {lang === "en" ? "No messages yet." : "Sin mensajes aun."}
                  </p>
                ) : (
                  guestTicketMessages.map((msg: any) => (
                    <Card key={msg.id} data-testid={`card-guest-msg-${msg.id}`}>
                      <CardContent className="p-3 space-y-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm font-medium" data-testid={`text-guest-msg-sender-${msg.id}`}>
                              {msg.senderName || (lang === "en" ? "Support" : "Soporte")}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm" data-testid={`text-guest-msg-body-${msg.id}`}>{msg.body}</p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
              {selectedGuestTicket.status !== "closed" && (
                <div className="space-y-2 pt-2 border-t">
                  <Input
                    value={guestReplyName}
                    onChange={(e) => setGuestReplyName(e.target.value)}
                    placeholder={lang === "en" ? "Your name" : "Su nombre"}
                    data-testid="input-guest-reply-name"
                  />
                  <Textarea
                    value={guestReplyText}
                    onChange={(e) => setGuestReplyText(e.target.value)}
                    placeholder={lang === "en" ? "Type your reply..." : "Escriba su respuesta..."}
                    className="resize-none"
                    data-testid="input-guest-reply"
                  />
                  <Button
                    className="w-full"
                    onClick={() => {
                      if (!guestReplyText.trim() || !guestReplyName.trim()) return;
                      guestReplyMutation.mutate({
                        ticketId: selectedGuestTicket.id,
                        body: guestReplyText.trim(),
                        guestName: guestReplyName.trim(),
                      });
                    }}
                    disabled={!guestReplyText.trim() || !guestReplyName.trim() || guestReplyMutation.isPending}
                    data-testid="button-guest-send-reply"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {guestReplyMutation.isPending
                      ? (lang === "en" ? "Sending..." : "Enviando...")
                      : (lang === "en" ? "Send Reply" : "Enviar Respuesta")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="space-y-3 mb-20">
        <Link href={`/g/${slug}/checkin${tokenParam}`}>
          <Button
            className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20"
            data-testid="button-start-checkin"
          >
            {lang === 'en' ? "Check In" : "Registrarse"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
        <Link href={`/g/${slug}/checkout${tokenParam}`}>
          <Button
            variant="outline"
            className="w-full h-12 text-base font-medium"
            data-testid="button-start-checkout"
          >
            {lang === 'en' ? "Start Checkout" : "Iniciar Salida"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>

      {token && (
        <>
          <GuestChat slug={slug} token={token} apartmentName={apartment.name} />
          <GuestLiveChat slug={slug} token={token} apartmentName={apartment.name} />
        </>
      )}
    </GuestLayout>
  );
}
